import { P2GText } from '@parcel2go/core-ui';
import { Account, QuoteAddress } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { FC, useMemo } from 'react';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import Section from '~/components/core/section/section';
import Address from '~/components/address-details/address/address';
import { BaseModuleProps } from './module-factory';
import useQuote from '~/hooks/use-quote';
import { isQuoteDropOff } from '~/lib/quotes';
import useSignIn from '~/hooks/use-signin';
import { useAuth } from 'react-oidc-context';
import { useAddressRequirements } from '~/hooks/use-address-requirements';
import SameAsAbove from '~/components/address-details/address/same-as-above';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';
import useAutoApplySavedAddress from '~/hooks/use-auto-apply-saved-address';

const OriginModule: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { settings } = useTenant();
  const { onSignInClick } = useSignIn();
  const { isAuthenticated } = useAuth();
  const { quote } = useQuote();
  const account = useBasketSection<Account>({ name: BasketSections.ACCOUNT });
  const isDropOff = isQuoteDropOff(quote);
  const { requirements, countries, loading, forceAddressSelection, unfilteredRequirementKeys } = useAddressRequirements(
    RequirementTypes.ORIGIN
  );

  const { processing } = useAutoApplySavedAddress(requirements);
  const section = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });

  const disabledFields = useMemo(() => {
    if (isAuthenticated || !section.data.metadata?.basedOnAccount) {
      return [];
    }
    const baseList = ['email', 'contactname'];
    return settings[PublicSettings.ACCOUNTMOBILE] === 'true' && account.data.mobile ? [...baseList, 'phone'] : baseList;
  }, [settings, section.data.metadata?.basedOnAccount, isAuthenticated, account.data.mobile]);

  const extras = quote?.availableExtras?.map((x) => x.key) || [];

  return wrapper(
    <Section
      title={t(isDropOff ? 'ORIGIN_RETURN_ADDRESS_TITLE' : 'ORIGIN_ADDRESS_TITLE', {
        defaultValue: isDropOff ? 'Return Address' : 'Collection Address'
      })}
      dataTestId="collection-address-section"
      loading={section.loading || loading || processing}
    >
      <Address
        disabledFields={disabledFields}
        unfilteredRequirementKeys={unfilteredRequirementKeys}
        signIn={onSignInClick}
        section={section}
        requirements={requirements}
        errors={errors}
        availableExtras={extras}
        type={BasketSections.ORIGIN}
        countries={countries}
        forceSelection={forceAddressSelection}
        sameAsAbove={
          !isAuthenticated && (
            <SameAsAbove
              checked={!!section.data.metadata?.basedOnAccount}
              onToggle={() => {
                section.update({
                  ...section.data,
                  metadata: { ...section.data.metadata, basedOnAccount: !section.data.metadata?.basedOnAccount }
                });
              }}
            />
          )
        }
        message={
          isDropOff && (
            <P2GText>
              <Trans i18nKey={`${BasketSections.ORIGIN}_ADDRESS_MESSAGE`.toUpperCase()} t={t}>
                As a safety measure, we would like to take your address in the unlikely event that there is a problem
                with the delivery of your item.
              </Trans>
            </P2GText>
          )
        }
      />
    </Section>
  );
};

export default OriginModule;
